import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { RefundAction } from '@itsrever/returns-api-types'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { XMarkIcon } from '@/ui/icons/xmark'
import { ExchangeIcon } from '@/ui/icons/exchange'
import {
  ApiReturnedItemSubproduct,
  ExchangedItemType,
  removeExchangeSelectionsByType,
  removeReturnedItem
} from '@/redux/return/return-slice'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { DeliveryTruckIcon } from '@/ui/icons/delivery-truck'

interface ItemActionProps {
  returnedItem: ApiReturnedItemSubproduct
}

export const ItemAction = ({ returnedItem }: ItemActionProps) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const [searchParams] = useSearchParams()

  const exchangeSelections = useAppSelector((store) => store.return.newItems)

  const returnedItemExchangeSelections = exchangeSelections.filter(
    (item) => item.returnedItemId === returnedItem.lineItemId
  )

  const variantExchange = returnedItemExchangeSelections.find(
    (item) => item.exchangeType === ExchangedItemType.ONE_ON_ONE
  )

  const fullCatalogExchanges = returnedItemExchangeSelections.filter(
    (item) => item.exchangeType === ExchangedItemType.FULL_CATALOG
  )

  const fullCatalogExchangesCount = fullCatalogExchanges.length

  const onRemoveReturnedItem = () => {
    if (returnedItem.lineItemId) {
      dispatch(
        removeReturnedItem({
          lineItemId: returnedItem.lineItemId ?? ''
        })
      )
    }
  }

  const onRemoveExchangeSelections = (type: ExchangedItemType) => {
    dispatch(
      removeExchangeSelectionsByType({
        lineItemId: returnedItem.lineItemId,
        exchangeType: type
      })
    )
  }

  const createQueryString = () => {
    const params = new URLSearchParams(searchParams.toString())

    params.set('lineItemId', returnedItem.lineItemId)
    params.set('catalog', 'true')
    params.set('cart', 'true')

    return params.toString()
  }

  return (
    <div className="flex min-h-11 items-center rounded-b-xl rounded-t-none border-t border-solid border-neutral-400/20 bg-neutral-50 px-2.5 py-2">
      {returnedItem.item.action === RefundAction.RefundAction_TO_RETURN && (
        <div className="flex flex-1 items-center justify-between">
          <div className="flex items-center gap-1.5 text-gray-800">
            <DeliveryTruckIcon width={18} height={18} />
            <p className="text-xs">
              {t('initial_selection.item_action_return', {
                count: returnedItem.item.quantity
              })}
            </p>
          </div>
          <button
            className="ml-auto rounded-sm p-1 transition-background hover:bg-neutral-200/30"
            aria-label="Remove returned item"
            onClick={onRemoveReturnedItem}
          >
            <XMarkIcon width={16} height={16} />
          </button>
        </div>
      )}
      {returnedItem.item.action === RefundAction.RefundAction_TO_EXCHANGE && (
        <div className="flex flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex shrink-0 items-center gap-1.5 text-gray-800">
            <ExchangeIcon width={16} height={16} />
            <p className="text-xs font-medium">
              {t('initial_selection.item_action_exchange', {
                count: returnedItem.item.quantity
              })}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {variantExchange ? (
              <div className="flex items-center gap-2 rounded-md border border-solid border-neutral-200 bg-white py-1 pl-2 pr-1.5">
                <p className="flex max-w-fit items-center text-xs">
                  <span className="block truncate">
                    {variantExchange?.productVariant.productName}
                  </span>
                  <span className="shrink-0">
                    , {variantExchange?.productVariant.variantName}
                  </span>
                </p>
                <button
                  className="relative ml-auto rounded-sm transition-background before:absolute before:inset-0 before:size-full before:scale-150 hover:bg-neutral-200/50"
                  aria-label="Remove exchange"
                  onClick={() =>
                    onRemoveExchangeSelections(ExchangedItemType.ONE_ON_ONE)
                  }
                >
                  <XMarkIcon width={16} height={16} />
                </button>
              </div>
            ) : null}
            {fullCatalogExchangesCount > 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-solid border-neutral-200 bg-white py-0.5 pl-0.5 pr-1.5">
                <ul className="flex gap-0.5">
                  {fullCatalogExchanges.map((item) => {
                    return (
                      <li
                        className="-ml-2 size-6 overflow-hidden rounded-sm border border-solid border-neutral-400/20 bg-white p-[0.1rem] first:ml-0 odd:-rotate-2 even:rotate-2"
                        key={item.productVariant.variantId}
                      >
                        <Link
                          to={{
                            search: '?' + createQueryString()
                          }}
                          aria-label="Go to cart"
                        >
                          {item.productVariant.imageUrl ? (
                            <img
                              src={item.productVariant.imageUrl}
                              width={20}
                              height={20}
                              alt={item.productVariant.productName}
                              className="h-auto w-full rounded-sm object-cover"
                            />
                          ) : (
                            <span
                              aria-hidden
                              className="flex size-full min-h-0 items-center justify-center bg-neutral-50"
                            >
                              <MediaImageIcon width={12} height={12} />
                            </span>
                          )}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
                <button
                  className="relative ml-auto rounded-sm transition-background before:absolute before:inset-0 before:size-full before:scale-150 hover:bg-neutral-200/50"
                  aria-label="Remove cart items"
                  onClick={() =>
                    onRemoveExchangeSelections(ExchangedItemType.FULL_CATALOG)
                  }
                >
                  <XMarkIcon width={16} height={16} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}
